import type {
  Aircraft as TreeAircraft,
  Operation as TreeOperation,
  Position as TreePosition,
  Role as TreeRole,
} from "./searchUtils";

/**
 * Tree-walk equivalent of the former flat form-selection filter.
 *
 * Before the relation tree moved server-side (CREW-530), the form selection was
 * applied to the flat relation rows and the tree was rebuilt from the
 * survivors. These rules reproduce that exact outcome on the assembled tree:
 *
 * - Roles selected → only those role subtrees survive.
 * - Position selected → role-level buckets/aircraft vanish (their rows carry no
 *   positionId), other positions vanish.
 * - Operations selected → role- and position-level buckets/aircraft vanish
 *   (their rows carry no operationId), other operations vanish.
 * - Aircraft selected → all non-aircraft buckets vanish everywhere; a selected
 *   designator keeps its whole node (own courses + all types); a selected type
 *   keeps its type node and leaves the parent designator as an empty shell.
 * - Emptied operations and positions are pruned (the old build step did this);
 *   emptied roles are left in — the caller's `hasContent` pass prunes them.
 */

interface AircraftSelectionItem {
  id: number;
  kind: "designator" | "type";
}

interface FormSelection {
  roles?: Array<{ id: number }>;
  position?: { id: number } | null;
  operations?: Array<{ id: number }>;
  aircraft?: AircraftSelectionItem[];
}

type AircraftTypeLookup = Map<number, { designatorId?: number | null }>;

const EMPTY_BUCKETS = { courses: [], customCourses: [], groups: [] };

const hasFormFilter = (selection: FormSelection | null | undefined): selection is FormSelection =>
  Boolean(
    selection?.roles?.length || selection?.position || selection?.operations?.length || selection?.aircraft?.length,
  );

const operationHasContent = (operation: TreeOperation) =>
  Boolean(
    operation.courses?.length ||
      operation.customCourses?.length ||
      operation.groups?.length ||
      operation.aircraft?.length,
  );

const positionHasContent = (position: TreePosition) =>
  Boolean(
    position.courses?.length ||
      position.customCourses?.length ||
      position.groups?.length ||
      position.operations?.length ||
      position.aircraft?.length,
  );

export const filterTreeByFormSelection = (
  roles: TreeRole[],
  selection: FormSelection | null | undefined,
  aircraftTypesById: AircraftTypeLookup,
): TreeRole[] => {
  if (!hasFormFilter(selection)) return roles;

  const roleIds = new Set(selection.roles?.map((role) => role.id) ?? []);
  const operationIds = new Set(selection.operations?.map((operation) => operation.id) ?? []);
  const selectedPosition = selection.position ?? null;
  const aircraftSelection = selection.aircraft ?? [];

  const typeMatches = (typeId: number) =>
    aircraftSelection.some((item) =>
      item.kind === "type" ? item.id === typeId : aircraftTypesById.get(typeId)?.designatorId === item.id,
    );

  const filterAircraftNodes = (nodes: TreeAircraft[] | undefined): TreeAircraft[] => {
    if (!aircraftSelection.length) return nodes ?? [];

    return (nodes ?? []).flatMap((node) => {
      const kind = (node as { kind?: "designator" | "type" }).kind;

      if (kind === "type") {
        return typeMatches(node.id) ? [node] : [];
      }

      // Designator node: selecting the designator keeps the whole node (its own
      // rows and every nested type match the flat filter); selecting only one of
      // its types keeps an empty shell with just that type.
      const designatorSelected = aircraftSelection.some((item) => item.kind === "designator" && item.id === node.id);
      if (designatorSelected) return [node];

      const keptTypes = (node.types ?? []).filter((type) => typeMatches(type.id));
      if (!keptTypes.length) return [];
      return [{ ...node, ...EMPTY_BUCKETS, types: keptTypes }];
    });
  };

  const filterBuckets = <T extends TreeRole | TreePosition | TreeOperation>(node: T, keepLevel: boolean): T =>
    keepLevel && !aircraftSelection.length ? node : { ...node, ...EMPTY_BUCKETS };

  return roles
    .filter((role) => !roleIds.size || roleIds.has(role.id))
    .map((role) => {
      // Role-level rows carry neither positionId nor operationId, so any
      // position/operation selection removes them.
      const keepRoleLevel = !selectedPosition && !operationIds.size;

      const positions = (role.positions ?? [])
        .filter((position) => !selectedPosition || position.id === selectedPosition.id)
        .map((position) => {
          // Position-level rows carry no operationId.
          const keepPositionLevel = !operationIds.size;

          const operations = (position.operations ?? [])
            .filter((operation) => !operationIds.size || operationIds.has(operation.id))
            .map((operation) => ({
              ...filterBuckets(operation, true),
              aircraft: filterAircraftNodes(operation.aircraft),
            }))
            .filter(operationHasContent);

          return {
            ...filterBuckets(position, keepPositionLevel),
            operations,
            aircraft: keepPositionLevel ? filterAircraftNodes(position.aircraft) : [],
          };
        })
        .filter(positionHasContent);

      return {
        ...filterBuckets(role, keepRoleLevel),
        positions,
        aircraft: keepRoleLevel ? filterAircraftNodes(role.aircraft) : [],
      };
    });
};
